/**
 * Copy dict, EN + IT. Ported from design-reference/index.html `window.COPY`.
 *
 * Every visible string in the app reads from one of these objects via `useLang()`.
 * Both languages must define exactly the same shape — TypeScript enforces this
 * via the `Copy` type below.
 */

import { CONTACT_EMAIL } from '@/lib/contact';

export type Lang = 'en' | 'it';

export interface CopyNav {
  yacht: string;
  location: string;
  availability: string;
  gallery: string;
  menu: string;
  menuOpen: string;
  menuClose: string;
  skipToContent: string;
}

export interface CopySpec {
  k: string;
  v: string;
  u: string;
  d: string;
}

export interface CopyInteriorScene {
  n: string;
  t: string;
  p: string;
  chips: string[];
}

export interface CopyContactCard {
  k: string;
  v: string;
  sub: string;
}

export interface CopyHero {
  eyebrow: string;
  title: [string, string, string];
  sub: string;
  side: string[];
  cue: string;
  cta: string;
}

/**
 * Home HeroScrub scene shape. Extends `CopyInteriorScene` with the CTA label
 * shown on the button below the chips. The button routes to `/yacht`
 * (no anchor) so every scene shares the same destination.
 */
export interface CopyHomeRevealScene extends CopyInteriorScene {
  cta: string;
}

export interface CopyHomeReveal {
  /** Small uppercase label above the scene stack (e.g. "On board") */
  eyebrow: string;
  scenes: CopyHomeRevealScene[];
}

/**
 * Yacht-page detail scene shape — the static "On board" sections on /yacht
 * that the Home HeroScrub buttons deep-link to. Each scene id must match its
 * sibling `CopyHomeRevealScene.id` so the anchor scroll lands correctly.
 */
export interface CopyYachtDetailScene {
  /** Roman numeral as a string, e.g. "I" .. "V" */
  n: string;
  /** Anchor slug — must match the matching `CopyHomeRevealScene.id` */
  id: string;
  /** Per-section eyebrow, e.g. "On board · 01" */
  eyebrow: string;
  /** Display title, e.g. "Sundeck." */
  t: string;
  /** Two or three paragraphs of body copy */
  paragraphs: string[];
  /** Micro-bullets rendered as chip pills */
  chips: string[];
}

export interface CopyYachtDetail {
  scenes: CopyYachtDetailScene[];
}

export interface CopyYacht {
  eyebrow: string;
  title: string;
  lede: string;
  specs: CopySpec[];
  rail: [string, string][];
}

export interface CopyInterior {
  eyebrow: string;
  title: string;
  lede: string;
  scenes: CopyInteriorScene[];
}

export interface CopyHomeInteriorScene {
  /** Anchor slug — stable id used as React key */
  id: string;
  /** Img alt text (also used as dot aria-label) */
  alt: string;
  /** Small uppercase label above the title, e.g. "Master cabin" */
  eyebrow: string;
  /** Display title for the scene */
  title: string;
  /** One short paragraph describing the space */
  body: string;
}

export interface CopyHomeInteriors {
  /** Section-level eyebrow, e.g. "Interiors" */
  eyebrow: string;
  /** Section-level display title, e.g. "Below decks." */
  title: string;
  scenes: CopyHomeInteriorScene[];
}

export interface CopyPortoCervoDestination {
  /** Display name on the map and card */
  name: string;
  /** Optional sub-label shown under the map name (e.g. anchorage name) */
  sub?: string;
  /** Short prose for the card */
  body: string;
}

export type PortoCervoDestId =
  | 'cala-di-volpe'
  | 'la-maddalena'
  | 'caprera'
  | 'spargi'
  | 'budelli';

export interface CopyPortoCervo {
  eyebrow: string;
  title: string;
  lede: string;
  /** Label below scale bar, e.g. "Approximate distances by sea" */
  footnote: string;
  /** Static label rendered as the map title (e.g. "Maddalena Archipelago") */
  mapTitle: string;
  destinations: Record<PortoCervoDestId, CopyPortoCervoDestination>;
}

export interface CopyBleed {
  title: string;
  side: string;
}

export interface CopyGallery {
  eyebrow: string;
  title: string;
  lede: string;
}

export interface CopyContactForm {
  name: string;
  namePh: string;
  email: string;
  emailPh: string;
  guests: string;
  date: string;
  duration: string;
  durations: string[];
  type: string;
  types: string[];
  message: string;
  messagePh: string;
  submit: string;
  note: string;
  sending: string;
  error: string;
  privacy: string;
}

export interface CopyContactSuccess {
  title: string;
  body: string;
  again: string;
}

/** Seasonal day-rate table shown in the inquiry aside. Static marketing strings
 *  (no Decimal/Intl formatting — there is no computed pricing); € is a prefix to
 *  match the site's house style. `note` states what the rate excludes. */
export interface CopyContactRate {
  label: string;
  seasons: { k: string; v: string }[];
  note: string;
  addon: string;
}

export interface CopyContact {
  eyebrow: string;
  title: string;
  lede: string;
  /** Label on the "See more" button under the home-CTA photo strip → /yacht */
  seeYacht: string;
  form: CopyContactForm;
  cards: CopyContactCard[];
  success: CopyContactSuccess;
  rate: CopyContactRate;
}

export interface CopyPageHero {
  eyebrow: string;
  title: string;
  lede: string;
}

export interface CopyAvailability {
  eyebrow: string;
  title: string;
  lede: string;
}

export interface CopyLocationCoordsItem {
  /** Uppercase small label, e.g. "Coordinates" */
  label: string;
  /** Display-font value, e.g. "41°08′N · 9°32′E" */
  value: string;
  /** Optional muted sub-line, e.g. "Pontile 11, Marina di Poltu Quatu" */
  sub?: string;
}

export interface CopyLocationFact {
  /** Uppercase small label, e.g. "Berth" */
  k: string;
  /** Display-font value, e.g. "Pontile 11" */
  v: string;
}

export interface CopyLocationIntro {
  eyebrow: string;
  title: string;
  /** 1-2 paragraphs of cove-and-village prose */
  body: string[];
  /** Caption shown under the framed inset image */
  caption: string;
  /** Alt text on the framed inset image */
  imageAlt: string;
}

export interface CopyLocationInquiry {
  eyebrow: string;
  title: string;
  body: string;
  /** CTA button label */
  cta: string;
}

export interface CopyLocationCoords {
  eyebrow: string;
  title: string;
  /** 2-3 paragraphs of Poltu Quatu prose (port name origin, geography, cruising waters) */
  body: string[];
  /** 5 tabular rows: lat/lon, depth, prevailing wind, water temperature, tides */
  items: CopyLocationCoordsItem[];
}

export interface CopyLocation {
  eyebrow: string;
  title: string;
  lede: string;
  /** Small details strip rendered below the hero lede (label/value pairs) */
  facts: CopyLocationFact[];
  /** Second section — typographic intro to Poltu Quatu with a framed inset image (replaces full-bleed <Bleed> on /location) */
  intro: CopyLocationIntro;
  /** Eyebrow + title above the sea-photo marquee section */
  strip: { eyebrow: string; title: string };
  /** Typographic block: prose left, coordinate table right */
  coords: CopyLocationCoords;
  /** Closing CTA section — replaces the text marquee on /location with a "Reserve your week" call-to-action routing to /availability */
  inquiry: CopyLocationInquiry;
}

export interface CopyPrivacySection {
  heading: string;
  body: string[];
  bullets?: string[];
}

export interface CopyPrivacy {
  eyebrow: string;
  title: string;
  updated: string;
  intro: string;
  sections: CopyPrivacySection[];
}

/** The not-found page (any address no route matches), EN at `/…`, IT at `/it/…`. */
export interface CopyNotFound {
  eyebrow: string;
  title: string;
  lede: string;
  /** Label of the one call to action: back to the home page in the same language. */
  home: string;
}

export interface Copy {
  nav: CopyNav;
  hero: CopyHero;
  homeReveal: CopyHomeReveal;
  marquee: string[];
  yacht: CopyYacht;
  yachtDetail: CopyYachtDetail;
  interior: CopyInterior;
  homeInteriors: CopyHomeInteriors;
  portoCervo: CopyPortoCervo;
  bleed: CopyBleed;
  gallery: CopyGallery;
  contact: CopyContact;
  yachtPage: CopyPageHero;
  locationPage: CopyLocation;
  availabilityPage: CopyAvailability;
  /** CTA label for the button overlaid on the bottom photo strip on /yacht. */
  yachtMore: string;
  footer: { rights: string; company: string; privacy: string };
  privacy: CopyPrivacy;
  notFound: CopyNotFound;
}

const EN: Copy = {
  nav: {
    yacht: 'The Yacht',
    location: 'Porto Cervo',
    availability: 'Availability',
    gallery: 'Gallery',
    menu: 'Menu',
    menuOpen: 'Open menu',
    menuClose: 'Close menu',
    skipToContent: 'Skip to main content',
  },
  hero: {
    eyebrow: 'Pershing 58 · Porto Cervo',
    title: ['Engineered', 'for the', 'Costa Smeralda.'],
    sub: 'A 17-metre Italian flagship for private charter from Porto Cervo. Day trips and short cruises on the Costa Smeralda.',
    side: [
      'Pershing 58',
      '17.7 m length',
      '42 knots top speed',
      '2 × MTU · 2,720 hp',
      'Surface drives',
      'Kevlar hull',
      'Up to 12 guests',
      'Hourly · daily · weekly',
    ],
    cue: 'Scroll to explore',
    cta: 'Start your inquiry',
  },
  homeReveal: {
    eyebrow: 'On board',
    scenes: [
      {
        n: 'I',
        cta: 'See more',
        t: 'Sundeck.',
        p: "A wraparound canopy of pale teak above the saloon. Sun pads fore and aft, bimini overhead.",
        chips: ['Sun pads', 'Bimini', 'Foredeck'],
      },
      {
        n: 'II',
        cta: 'See more',
        t: 'Bow lounge.',
        p: 'A U-lounge in the foredeck. Quiet moorings in Cala di Volpe, aperitivi at sunset.',
        chips: ['U-lounge', 'Foredeck', 'At anchor'],
      },
      {
        n: 'III',
        cta: 'See more',
        t: 'Underway.',
        p: 'Twin MTU surface drives, 36 knots on plane. Porto Cervo to La Maddalena in under an hour.',
        chips: ['36 kn cruise', 'MTU diesels', 'Surface drives'],
      },
      {
        n: 'IV',
        cta: 'See more',
        t: 'Helm.',
        p: "Triple-screen Garmin under the hardtop. Leather chairs, glass on three sides.",
        chips: ['Triple-screen', 'Garmin', 'Hardtop'],
      },
      {
        n: 'V',
        cta: 'See more',
        t: 'Below decks.',
        p: "A full-beam owner's suite in pale oak and ivory linen. Ensuite with twin basins and a rain shower.",
        chips: ['King berth', 'Ensuite', 'Climate'],
      },
    ],
  },
  marquee: [
    'Pershing 58',
    'Porto Cervo',
    'La Maddalena',
    'Costa Smeralda',
    'Caprera',
    'Spargi',
    'Italian Engineering',
  ],
  yacht: {
    eyebrow: '01 · The Yacht',
    title: 'Fast, and built to stay that way.',
    lede: 'Twin MTU diesels, surface drives, a Kevlar hull. 42 knots top, 36 at cruise. Bleached teak, polished steel, ivory leather.',
    specs: [
      { k: 'Length', v: '17.7', u: 'm', d: 'Overall length, 58 ft' },
      { k: 'Beam', v: '4.30', u: 'm', d: 'Stable at speed and at anchor' },
      { k: 'Speed', v: '42', u: 'kn', d: 'Top speed · 36 kn cruise' },
      { k: 'Engines', v: '2×', u: 'MTU', d: '1,360 hp surface drives' },
      { k: 'Guests', v: '12', u: 'day', d: 'Daytime charter · two crew' },
      { k: 'Year', v: '2013', u: 'build', d: 'Italian-built Pershing 58' },
      { k: 'Fuel', v: '2,200', u: 'L', d: 'Range 320 NM at 30 kn' },
      { k: 'Tender', v: '3.4', u: 'm Williams', d: 'Jet tender with watersports kit' },
    ],
    rail: [
      ['Berth', 'Marina di Poltu Quatu · Sardinia'],
      ['Captain', 'MCA Master 200'],
      ['Crew', 'Captain + stewardess included'],
      ['Languages', 'Italian · English · French'],
      ['Included', 'Fuel up to 4h, water, ice, towels, wifi'],
      ['On request', 'Chef, sommelier, watertoys, transfers'],
    ],
  },
  yachtDetail: {
    scenes: [
      {
        n: 'I',
        id: 'sundeck',
        eyebrow: 'On board · 01',
        t: 'Sundeck.',
        paragraphs: [
          "Above the saloon, a wraparound expanse of pale teak. Sun pads fore and aft, a bimini overhead.",
          "Twin sun pads forward of the helm, a second pair aft. The whole upper deck becomes one open lounge.",
        ],
        chips: ['Teak deck', 'Bimini', 'Sun pads', 'Hardtop'],
      },
      {
        n: 'II',
        id: 'bow-lounge',
        eyebrow: 'On board · 02',
        t: 'Bow lounge.',
        paragraphs: [
          "The foredeck folds into a U-lounge below the bow rail. Quiet moorings in Cala di Volpe, aperitivi at sunset.",
          "At anchor it's the most private corner aboard. The bow points into the wind, the cockpit two decks away.",
        ],
        chips: ['U-lounge', 'Foredeck', 'At anchor', 'Cala di Volpe'],
      },
      {
        n: 'III',
        id: 'underway',
        eyebrow: 'On board · 03',
        t: 'Underway.',
        paragraphs: [
          "Twin MTU diesels and surface drives put the Pershing 58 on plane at 36 knots. Porto Cervo to La Maddalena in under an hour, Caprera or Spargi inside a morning.",
          "The Kevlar hull holds steady at speed. 42 knots flat out, happiest between 30 and 36.",
        ],
        chips: ['36 kn cruise', '42 kn top', 'MTU diesels', 'Surface drives', 'Kevlar hull'],
      },
      {
        n: 'IV',
        id: 'helm',
        eyebrow: 'On board · 04',
        t: 'Helm.',
        paragraphs: [
          "Triple-screen Garmin under the hardtop. Leather captain's chairs, glass on three sides. AIS, radar, autopilot and depth-sounder, all at a glance.",
          "At the helm, an MCA Master 200 captain. The crew comes with the boat.",
        ],
        chips: ['Triple-screen Garmin', 'Hardtop', "Captain's chairs", 'AIS · radar · autopilot'],
      },
      {
        n: 'V',
        id: 'below-decks',
        eyebrow: 'On board · 05',
        t: 'Below decks.',
        paragraphs: [
          "A full-beam owner's suite amidships, in pale oak and ivory linen. Hull windows run nearly the length of the cabin. King berth, walk-in wardrobe, ensuite with twin basins and a rain shower.",
          "A VIP cabin forward with a convertible queen, and a twin guest cabin. Crew quarters are aft and self-contained.",
        ],
        chips: ['Owner suite', 'Ensuite', 'King berth', 'Twin basins', 'Rain shower'],
      },
    ],
  },
  interior: {
    eyebrow: '02 · Interior',
    title: 'Below decks.',
    lede: 'Bleached oak, cream leather, soft daylight through tinted glass. The Pershing 58 carries her glamour quietly.',
    scenes: [
      {
        n: 'I',
        t: 'Master cabin.',
        p: "A full-beam owner's suite amidships, dressed in ivory linens and pale oak. Ensuite head with twin basins and a rain shower.",
        chips: ['King berth', 'Ensuite', 'Climate'],
      },
      {
        n: 'II',
        t: 'Saloon & galley.',
        p: 'Open-plan dining for six, a fully appointed galley aft, and a horizon of teak and glass on either side.',
        chips: ['Seats 6', 'Wine cellar', 'Sound system'],
      },
      {
        n: 'III',
        t: 'Helm & sundeck.',
        p: "Triple-screen Garmin helm, leather captain's chairs, and a sundeck designed to disappear into the sea behind you.",
        chips: ['Wraparound', 'Bimini', 'Sun pads'],
      },
      {
        n: 'IV',
        t: 'Aft cockpit.',
        p: 'Wraparound lounges around a teak table. Long lunches at anchor, sunset aperitivi, dinner under the stars off Cala di Volpe.',
        chips: ['Dining 8', 'Shaded', 'Teak deck'],
      },
    ],
  },
  homeInteriors: {
    eyebrow: 'Interiors',
    title: 'Inside, the boat softens.',
    scenes: [
      {
        id: 'master-cabin',
        alt: 'Master cabin, pale oak and ivory leather',
        eyebrow: 'Master cabin',
        title: 'Pale oak, ivory leather.',
        body: "Full-beam owner's suite amidships, hand-stitched headboard and hull windows on both sides. Sea light at anchor, complete privacy underway.",
      },
      {
        id: 'forward-stateroom',
        alt: 'Forward VIP stateroom with convertible queen',
        eyebrow: 'Forward stateroom',
        title: 'VIP forward.',
        body: 'Convertible queen with its own ensuite, generous headroom under the deckhouse, and a panoramic forward hatch for natural ventilation.',
      },
      {
        id: 'saloon-galley',
        alt: 'Saloon and galley with tinted-glass horizon',
        eyebrow: 'Saloon & galley',
        title: 'Open-plan, glass on every side.',
        body: 'Dining for six and a fully appointed galley sit under a horizon of tinted glass. A quiet living room moving across the sea.',
      },
      {
        id: 'ensuite',
        alt: 'Ensuite head with twin Carrara basins',
        eyebrow: 'Ensuite',
        title: 'Carrara, twin basins.',
        body: 'Honed Carrara counter, twin basins and a separate rainfall shower. Polished steel fittings throughout. Standard across both suites.',
      },
    ],
  },
  portoCervo: {
    eyebrow: 'Costa Smeralda',
    title: 'Sea you don’t forget.',
    lede: 'Based at Marina di Poltu Quatu. Half an hour to the Maddalena archipelago and the cleanest water in the Mediterranean.',
    footnote: 'Approximate distances by sea from Marina di Poltu Quatu. Routing varies with sea state and traffic.',
    mapTitle: 'Maddalena Archipelago · 41°N',
    destinations: {
      'cala-di-volpe': {
        name: 'Cala di Volpe',
        sub: 'Costa Smeralda',
        body: 'The signature anchorage of the Costa Smeralda, five minutes south of the marina. Granite outcrops, emerald water, the most photographed bay in Sardinia.',
      },
      'la-maddalena': {
        name: 'La Maddalena',
        sub: 'Town & harbour',
        body: "The archipelago's main town. A working harbour and the natural lunch stop heading north.",
      },
      caprera: {
        name: 'Caprera',
        sub: 'Cala Coticcio (Tahiti)',
        body: "Sardinia's Tahiti. White granite sand, water the colour of a pool, reachable only by boat.",
      },
      spargi: {
        name: 'Spargi',
        sub: 'Cala Corsara',
        body: 'The wildest anchorage in the archipelago. Cala Corsara holds in any wind, its sandbar turning turquoise at noon.',
      },
      budelli: {
        name: 'Budelli',
        sub: 'Spiaggia Rosa',
        body: 'The pink-sand beach, protected and offshore-only. Anchor between Budelli and Razzoli, in glass-clear water.',
      },
    },
  },
  bleed: {
    title: 'From Porto Cervo to La Maddalena in under an hour.',
    side: 'Day charters · Sunset cruises · Multi-day itineraries',
  },
  gallery: {
    eyebrow: '03 · Portfolio',
    title: 'A portrait of the boat.',
    lede: 'Fifty frames captured in late spring across the Costa Smeralda. Hover any image to view full-size.',
  },
  contact: {
    eyebrow: '04 · Inquiry',
    title: 'Plan your charter.',
    lede: 'Tell us your dates and the kind of trip you have in mind. We reply within 24 hours.',
    seeYacht: 'See more',
    form: {
      name: 'Full name',
      namePh: 'Marco Rossi',
      email: 'Email',
      emailPh: 'marco@example.com',
      guests: 'Guests',
      date: 'Preferred date',
      duration: 'Duration',
      durations: ['Half day (4h)', 'Full day (8h)', 'Weekend (2 days)', 'Week', 'Custom'],
      type: 'Charter type',
      types: ['Day experience', 'Sunset cruise', 'Swim & lunch at anchor', 'Multi-day itinerary', 'Special event', 'Other'],
      message: 'Tell us more',
      messagePh: 'Group composition, occasion, departure marina, dietary notes…',
      submit: 'Send inquiry',
      note: 'By submitting you accept our courtesy reply within 24h. No payment is taken at this stage.',
      sending: 'Sending…',
      error: 'We were unable to send your inquiry. Please email us directly at',
      privacy: 'Your details are sent via our form provider (Formspree) only to handle your inquiry.',
    },
    cards: [
      { k: 'Email', v: CONTACT_EMAIL, sub: 'Bookings & itineraries' },
      { k: 'Berth', v: 'Marina di Poltu Quatu · Pontile 11', sub: 'Costa Smeralda, Sardinia' },
    ],
    success: {
      title: 'Grazie.',
      body: "Your inquiry has reached us. We'll reply within 24 hours with availability, a quote and a suggested itinerary.",
      again: 'Send another inquiry',
    },
    rate: {
      label: 'Daily rate',
      seasons: [
        { k: 'Low season', v: '€3,800' },
        { k: 'July', v: '€4,900' },
        { k: 'August', v: '€5,500' },
      ],
      note: 'Fuel & crew not included',
      addon: 'Seabob — €250 / day (optional)',
    },
  },
  yachtPage: {
    eyebrow: 'The Yacht',
    title: 'A Pershing 58, Italian to the core.',
    lede: 'A 17.7-metre planing hardtop. Twin MTU diesels, surface drives, a Kevlar hull. Built in 2013, Italian to the core.',
  },
  locationPage: {
    eyebrow: 'Porto Cervo',
    title: 'Porto Cervo, Costa Smeralda.',
    lede: 'A protected granite cove on the Costa Smeralda. The Pershing 58 is moored at Pontile 11, sheltered on three sides, half an hour from the Maddalena archipelago.',
    facts: [
      { k: 'Berth', v: 'Pontile 11' },
      { k: 'Marina', v: 'Poltu Quatu' },
      { k: 'Region', v: 'Costa Smeralda' },
      { k: 'Season', v: 'May – October' },
    ],
    intro: {
      eyebrow: 'The cove',
      title: 'A cove in the granite.',
      body: [
        'The marina sits in a cleft of pink granite cut into the Costa Smeralda, a narrow canyon you only notice from sea level. Past the rock pillars at the entrance, the cove holds flat in any weather.',
        'The village above is small and high-end: one piazza, a few restaurants, a boutique hotel. Guests drop their bags at Hotel Pitrizza or Cala di Volpe and walk five minutes to the dock; the boat is provisioned, fuelled and ready on arrival.',
      ],
      caption: 'Poltu Quatu · marina at dawn',
      imageAlt: 'Marina di Poltu Quatu at dawn, the boat at its berth',
    },
    strip: {
      eyebrow: 'On the water',
      title: 'From the cove to open water.',
    },
    coords: {
      eyebrow: 'Cruising waters',
      title: 'Where she runs.',
      body: [
        'The Pershing 58 berths at Pontile 11 of the Marina di Poltu Quatu, a narrow cove in the granite headland. It\'s protected from the open sea on three sides, a short walk from the village.',
        'The waters open onto the Bocche di Bonifacio and the Maddalena archipelago: five protected islands within an hour, granite outcrops, white sand and shallow water. The Maestrale blows from the north-west in the afternoons.',
        'Best shelter is in the lee of Caprera, Spargi and Budelli, where the depth holds and the anchor sets first time.',
      ],
      items: [
        { label: 'Coordinates', value: '41°08′N · 9°32′E', sub: 'Pontile 11, Marina di Poltu Quatu' },
        { label: 'Depth at berth', value: '10–40 m', sub: 'Granite + Posidonia oceanica' },
        { label: 'Prevailing wind', value: 'Maestrale W–NW', sub: '8–15 kn summer · gusts to 30' },
        { label: 'Water temperature', value: '22–26 °C', sub: 'June through September' },
        { label: 'Tides', value: '< 30 cm', sub: 'Negligible · Mediterranean basin' },
      ],
    },
    inquiry: {
      eyebrow: 'Reserve',
      title: 'Plan your charter.',
      body: 'Tell us your dates, your group, and the kind of trip you have in mind. We reply within 24 hours with availability, a quote and an itinerary.',
      cta: 'Start your inquiry',
    },
  },
  availabilityPage: {
    eyebrow: 'Availability',
    title: 'Check your dates.',
    lede: "Live availability is on request. Send your preferred dates below and we'll confirm within 24 hours.",
  },
  yachtMore: 'More of the yacht',
  footer: {
    rights: '© 2026 Riva Immobiliare S.r.l.',
    company: "P. IVA 13256780969 · Via Antonio Locatelli 14, 20851 Lissone (MB)",
    privacy: 'Privacy',
  },
  notFound: {
    eyebrow: 'Error 404',
    title: 'Page not found.',
    lede: 'The address you followed does not exist on this site. The Pershing 58 is waiting for you in Porto Cervo.',
    home: 'Back to home',
  },
  privacy: {
    eyebrow: 'Legal',
    title: 'Privacy Policy',
    updated: 'Last updated: 31 May 2026',
    intro: `This Privacy Policy explains how Riva Immobiliare S.r.l. ("we", "us") processes personal data when you visit this website and when you send us a charter inquiry. This site presents the private yacht-charter offering operated under the "Virtus" name by Riva Immobiliare S.r.l.`,
    sections: [
      {
        heading: 'Data controller',
        body: [
          `The data controller is Riva Immobiliare S.r.l., Via Antonio Locatelli 14, 20851 Lissone (MB), Italy. VAT number (P. IVA) 13256780969.`,
          `For any question about this policy or about your personal data, you can contact us at giorgio.l@virtussrl.it.`,
        ],
      },
      {
        heading: 'What data we collect',
        body: [
          `We only process the data you choose to give us, plus the minimal technical data needed to serve the site:`,
        ],
        bullets: [
          `Inquiry form: your name, email address, number of guests, preferred date, charter duration, charter type and any message you write. These are sent only when you submit the form.`,
          `Technical data: when your browser loads the site, your IP address and standard connection data are processed by our hosting and content-delivery providers in order to deliver and protect the site.`,
          `Language preference: we store your chosen language (English or Italian) in your browser's local storage. It contains no personal data and is used only to remember your choice.`,
        ],
      },
      {
        heading: 'Why we use it, and the legal basis',
        body: [`We process your data on the following legal bases under the GDPR:`],
        bullets: [
          `To answer your inquiry and contact you about a possible charter. Legal basis: steps taken at your request before entering into a contract, and our legitimate interest in responding (Art. 6(1)(b) and (f) GDPR).`,
          `To deliver, secure and maintain the website. Legal basis: our legitimate interest in running a safe, working site (Art. 6(1)(f) GDPR).`,
        ],
      },
      {
        heading: 'Who receives your data',
        body: [
          `We do not sell your data and we do not use it for advertising. We rely on a small number of service providers:`,
        ],
        bullets: [
          `Formspree (Formspree, Inc., United States) receives your inquiry-form submission and relays it to our email inbox. Your form data is processed in the United States.`,
          `Email: your inquiry is delivered to our inbox at giorgio.l@virtussrl.it. If the form cannot be sent, the site offers a fallback that opens your own email program with a message already addressed to the same inbox.`,
          `Cloudflare: the content-delivery and security layer that processes connection data (including your IP address) to deliver and protect the site.`,
          `Google Fonts (Google): the site loads its typefaces from Google's servers; when it does, your IP address is transmitted to Google.`,
        ],
      },
      {
        heading: 'Transfers outside the EU',
        body: [
          `Some of the providers above (such as Formspree and Google) are based in, or transfer data to, the United States. Where this happens, the transfer relies on appropriate safeguards under the GDPR, such as the European Commission's Standard Contractual Clauses or the EU–US Data Privacy Framework.`,
        ],
      },
      {
        heading: 'How long we keep it',
        body: [
          `We keep your inquiry and the related correspondence only for as long as needed to handle your request and to keep reasonable records of our business contacts, after which it is deleted. The language preference stays in your browser until you clear it.`,
        ],
      },
      {
        heading: 'Your rights',
        body: [`Under the GDPR you have the right to:`],
        bullets: [
          `access your personal data and obtain a copy of it;`,
          `have inaccurate data corrected and incomplete data completed;`,
          `have your data erased, or its processing restricted;`,
          `object to processing based on our legitimate interest;`,
          `receive your data in a portable, machine-readable format.`,
        ],
      },
      {
        heading: 'How to exercise your rights, and complaints',
        body: [
          `To exercise any of these rights, write to us at giorgio.l@virtussrl.it. You also have the right to lodge a complaint with the Italian Data Protection Authority (Garante per la protezione dei dati personali, www.garanteprivacy.it).`,
        ],
      },
      {
        heading: 'Cookies and tracking',
        body: [
          `This site does not use tracking or advertising cookies, and it does not use any analytics. The only information stored in your browser is the functional language-preference item described above.`,
        ],
      },
      {
        heading: 'Changes to this policy',
        body: [
          `We may update this policy from time to time. The version published on this page is always the current one, and the date shown at the top indicates when it was last changed.`,
        ],
      },
    ],
  },
};

const IT: Copy = {
  nav: {
    yacht: 'Lo Yacht',
    location: 'Porto Cervo',
    availability: 'Disponibilità',
    gallery: 'Galleria',
    menu: 'Menu',
    menuOpen: 'Apri il menu',
    menuClose: 'Chiudi il menu',
    skipToContent: 'Vai al contenuto principale',
  },
  hero: {
    eyebrow: 'Pershing 58 · Porto Cervo',
    title: ['Progettato', 'per la', 'Costa Smeralda.'],
    sub: "Un'ammiraglia italiana di 17 metri in charter privato da Porto Cervo. Gite giornaliere e brevi crociere in Costa Smeralda.",
    side: [
      'Pershing 58',
      '17,7 m di lunghezza',
      '42 nodi massima',
      '2 × MTU · 2.720 hp',
      'Eliche di superficie',
      'Scafo in Kevlar',
      'Fino a 12 ospiti',
      'Orario · giornaliero · settimanale',
    ],
    cue: 'Scorri per esplorare',
    cta: 'Inizia la richiesta',
  },
  homeReveal: {
    eyebrow: 'A bordo',
    scenes: [
      {
        n: 'I',
        cta: 'Vedi di più',
        t: 'Sundeck.',
        p: "Sopra il salone, una distesa di teak chiaro a tutto giro. Prendisole a prua e a poppa, bimini all'ombra.",
        chips: ['Prendisole', 'Bimini', 'Salotto di prua'],
      },
      {
        n: 'II',
        cta: 'Vedi di più',
        t: 'Salotto di prua.',
        p: 'Un divano a U sul ponte di prua. Soste tranquille a Cala di Volpe, aperitivi al tramonto.',
        chips: ['Divano a U', 'Ponte di prua', 'Alla fonda'],
      },
      {
        n: 'III',
        cta: 'Vedi di più',
        t: 'In navigazione.',
        p: "Due MTU con eliche di superficie, 36 nodi in planata. Da Porto Cervo a La Maddalena in meno di un'ora.",
        chips: ['36 nodi crociera', 'Motori MTU', 'Eliche di superficie'],
      },
      {
        n: 'IV',
        cta: 'Vedi di più',
        t: 'Plancia.',
        p: "Plancia Garmin a tre schermi sotto l'hardtop. Poltrone in pelle, vetro su tre lati.",
        chips: ['Tre schermi', 'Garmin', 'Hardtop'],
      },
      {
        n: 'V',
        cta: 'Vedi di più',
        t: 'Sotto coperta.',
        p: 'Cabina armatoriale a baglio massimo, rovere chiaro e lini avorio. Bagno privato con doppio lavabo e doccia a pioggia.',
        chips: ['Letto king', 'Bagno privato', 'Climatizzata'],
      },
    ],
  },
  marquee: [
    'Pershing 58',
    'Porto Cervo',
    'La Maddalena',
    'Costa Smeralda',
    'Caprera',
    'Spargi',
    'Ingegneria italiana',
  ],
  yacht: {
    eyebrow: '01 · Lo Yacht',
    title: 'Veloce, e costruito per restare tale.',
    lede: 'Due diesel MTU, eliche di superficie, scafo in Kevlar. 42 nodi di massima, 36 di crociera. Teak sbiancato, acciaio lucido, pelle avorio.',
    specs: [
      { k: 'Lunghezza', v: '17,7', u: 'm', d: 'Lunghezza fuori tutto, 58 ft' },
      { k: 'Larghezza', v: '4,30', u: 'm', d: 'Stabile in navigazione e alla fonda' },
      { k: 'Velocità', v: '42', u: 'nodi', d: 'Massima · 36 nodi crociera' },
      { k: 'Motori', v: '2×', u: 'MTU', d: '1.360 hp eliche di superficie' },
      { k: 'Ospiti', v: '12', u: 'giorno', d: 'Charter diurno · due di equipaggio' },
      { k: 'Anno', v: '2013', u: '', d: 'Pershing 58 di costruzione italiana' },
      { k: 'Carburante', v: '2.200', u: 'L', d: 'Autonomia 320 NM a 30 nodi' },
      { k: 'Tender', v: '3,4', u: 'm Williams', d: 'Tender jet con kit watersports' },
    ],
    rail: [
      ['Ormeggio', 'Marina di Poltu Quatu · Sardegna'],
      ['Comandante', 'MCA Master 200'],
      ['Equipaggio', 'Comandante + hostess inclusi'],
      ['Lingue', 'Italiano · Inglese · Francese'],
      ['Incluso', 'Carburante fino a 4h, acqua, ghiaccio, asciugamani, wifi'],
      ['A richiesta', 'Chef, sommelier, watertoys, transfer'],
    ],
  },
  yachtDetail: {
    scenes: [
      {
        n: 'I',
        id: 'sundeck',
        eyebrow: 'A bordo · 01',
        t: 'Sundeck.',
        paragraphs: [
          "Sopra il salone, una distesa di teak chiaro a tutto giro. Prendisole a prua e a poppa, un bimini per l'ombra.",
          "Due prendisole a proravia della plancia, altri due a poppa. Tutto il ponte superiore diventa un'unica zona lounge.",
        ],
        chips: ['Teak', 'Bimini', 'Prendisole', 'Hardtop'],
      },
      {
        n: 'II',
        id: 'bow-lounge',
        eyebrow: 'A bordo · 02',
        t: 'Salotto di prua.',
        paragraphs: [
          "Il ponte di prua si apre in un divano a U sotto la battagliola. Soste tranquille a Cala di Volpe, aperitivi al tramonto.",
          "Alla fonda è l'angolo più riservato della barca. La prua punta al vento, il pozzetto due ponti più in basso.",
        ],
        chips: ['Divano a U', 'Ponte di prua', 'Alla fonda', 'Cala di Volpe'],
      },
      {
        n: 'III',
        id: 'underway',
        eyebrow: 'A bordo · 03',
        t: 'In navigazione.',
        paragraphs: [
          "Due diesel MTU ed eliche di superficie mettono il Pershing 58 in planata a 36 nodi. Da Porto Cervo a La Maddalena in meno di un'ora, Caprera o Spargi in una mattinata.",
          "Lo scafo in Kevlar resta saldo in velocità. 42 nodi di massima, al meglio tra i 30 e i 36.",
        ],
        chips: ['36 nodi crociera', '42 nodi massima', 'Motori MTU', 'Eliche di superficie', 'Scafo Kevlar'],
      },
      {
        n: 'IV',
        id: 'helm',
        eyebrow: 'A bordo · 04',
        t: 'Plancia.',
        paragraphs: [
          "Plancia Garmin a tre schermi sotto l'hardtop. Poltrone in pelle, vetro su tre lati. AIS, radar, pilota automatico ed ecoscandaglio, tutto a colpo d'occhio.",
          "Al timone, un comandante MCA Master 200. L'equipaggio è incluso.",
        ],
        chips: ['Tre schermi Garmin', 'Hardtop', 'Poltrone in pelle', 'AIS · radar · pilota auto'],
      },
      {
        n: 'V',
        id: 'below-decks',
        eyebrow: 'A bordo · 05',
        t: 'Sotto coperta.',
        paragraphs: [
          "Cabina armatoriale a baglio massimo a centro barca, in rovere chiaro e lini avorio. Le finestre di murata corrono quasi per tutta la cabina. Letto king, cabina armadio, bagno privato con doppio lavabo e doccia a pioggia.",
          "A prua una cabina VIP con letto matrimoniale convertibile e una cabina ospiti con letti gemelli. Le cabine equipaggio sono a poppa e indipendenti.",
        ],
        chips: ['Cabina armatoriale', 'Bagno privato', 'Letto king', 'Doppio lavabo', 'Doccia a pioggia'],
      },
    ],
  },
  interior: {
    eyebrow: '02 · Interni',
    title: 'Sotto coperta.',
    lede: 'Rovere sbiancato, pelle crema, luce filtrata dai vetri scuri. Il Pershing 58 porta la sua eleganza con discrezione.',
    scenes: [
      {
        n: 'I',
        t: 'Cabina armatoriale.',
        p: 'Cabina a baglio massimo a centro barca, lini avorio e rovere chiaro. Bagno privato con doppio lavabo e doccia a pioggia.',
        chips: ['Letto king', 'Bagno privato', 'Climatizzata'],
      },
      {
        n: 'II',
        t: 'Salone e cucina.',
        p: 'Dinette per sei, cucina completa a poppa, e un orizzonte di teak e cristallo da entrambi i lati.',
        chips: ['6 posti', 'Cantina', 'Impianto audio'],
      },
      {
        n: 'III',
        t: 'Plancia e sundeck.',
        p: 'Plancia Garmin a tre schermi, poltrone in pelle, e un sundeck che svanisce nel mare alle tue spalle.',
        chips: ['Panoramica', 'Bimini', 'Prendisole'],
      },
      {
        n: 'IV',
        t: 'Pozzetto.',
        p: "Divani a U attorno al tavolo in teak. Pranzi alla fonda, aperitivi al tramonto, cene sotto le stelle a Cala di Volpe.",
        chips: ['8 a tavola', "All'ombra", 'Coperta in teak'],
      },
    ],
  },
  homeInteriors: {
    eyebrow: 'Interni',
    title: 'Dentro, la barca si addolcisce.',
    scenes: [
      {
        id: 'master-cabin',
        alt: 'Cabina armatoriale, rovere chiaro e pelle avorio',
        eyebrow: 'Cabina armatoriale',
        title: 'Rovere chiaro, pelle avorio.',
        body: 'Cabina a baglio massimo, testiera cucita a mano e finestre laterali sullo scafo. Luce di mare alla fonda, privacy assoluta in navigazione.',
      },
      {
        id: 'forward-stateroom',
        alt: 'Cabina VIP a prua con letto matrimoniale convertibile',
        eyebrow: 'Cabina VIP',
        title: 'VIP a prua.',
        body: 'Letto matrimoniale convertibile con bagno privato, altezza generosa sotto la tuga e boccaporto panoramico per la ventilazione naturale.',
      },
      {
        id: 'saloon-galley',
        alt: 'Salone e cucina con orizzonte di cristallo fumé',
        eyebrow: 'Salone e cucina',
        title: 'Open-plan, vetri su ogni lato.',
        body: 'Dinette per sei e cucina completa sotto un orizzonte di cristallo fumé. Un soggiorno tranquillo che si muove sul mare.',
      },
      {
        id: 'ensuite',
        alt: 'Bagno privato con doppio lavabo in Carrara',
        eyebrow: 'Bagno privato',
        title: 'Carrara, doppio lavabo.',
        body: 'Piano in Carrara lavorato, doppio lavabo e doccia a pioggia separata. Rubinetteria in acciaio lucido. Standard in entrambe le suite.',
      },
    ],
  },
  portoCervo: {
    eyebrow: 'Costa Smeralda',
    title: 'Un mare che non si scorda.',
    lede: 'Di base alla Marina di Poltu Quatu. A mezz’ora dall’arcipelago della Maddalena e dall’acqua più pulita del Mediterraneo.',
    footnote: 'Distanze approssimative via mare dalla Marina di Poltu Quatu. La rotta varia con il mare e il traffico.',
    mapTitle: 'Arcipelago della Maddalena · 41°N',
    destinations: {
      'cala-di-volpe': {
        name: 'Cala di Volpe',
        sub: 'Costa Smeralda',
        body: 'L’ancoraggio simbolo della Costa Smeralda, cinque minuti a sud della marina. Graniti, acqua smeraldo, la baia più fotografata della Sardegna.',
      },
      'la-maddalena': {
        name: 'La Maddalena',
        sub: 'Paese e porto',
        body: 'Il centro principale dell’arcipelago. Un porto vivo e la tappa pranzo naturale verso nord.',
      },
      caprera: {
        name: 'Caprera',
        sub: 'Cala Coticcio (Tahiti)',
        body: 'La Tahiti sarda. Sabbia bianca di granito, acqua color piscina, raggiungibile solo via mare.',
      },
      spargi: {
        name: 'Spargi',
        sub: 'Cala Corsara',
        body: 'L’ancoraggio più selvaggio dell’arcipelago. Cala Corsara tiene con ogni vento, con una secca che diventa turchese a mezzogiorno.',
      },
      budelli: {
        name: 'Budelli',
        sub: 'Spiaggia Rosa',
        body: 'La spiaggia rosa, protetta e raggiungibile solo via mare. Ormeggio tra Budelli e Razzoli, in acqua cristallina.',
      },
    },
  },
  bleed: {
    title: "Da Porto Cervo a La Maddalena in meno di un'ora.",
    side: 'Charter giornaliero · Tramonto · Itinerari multi-giorno',
  },
  gallery: {
    eyebrow: '03 · Portfolio',
    title: 'Un ritratto della barca.',
    lede: "Cinquanta scatti realizzati a tarda primavera sulla Costa Smeralda. Passa il mouse su un'immagine per ingrandirla.",
  },
  contact: {
    eyebrow: '04 · Richiesta',
    title: 'Organizza il tuo charter.',
    lede: 'Raccontaci le date e che tipo di esperienza hai in mente. Rispondiamo entro 24 ore.',
    seeYacht: 'Vedi di più',
    form: {
      name: 'Nome completo',
      namePh: 'Marco Rossi',
      email: 'Email',
      emailPh: 'marco@esempio.it',
      guests: 'Ospiti',
      date: 'Data preferita',
      duration: 'Durata',
      durations: ['Mezza giornata (4h)', 'Giornaliero (8h)', 'Weekend (2 giorni)', 'Settimana', 'Personalizzato'],
      type: 'Tipo charter',
      types: ['Esperienza giornaliera', 'Crociera al tramonto', 'Bagno e pranzo alla fonda', 'Itinerario multi-giorno', 'Evento speciale', 'Altro'],
      message: 'Raccontaci di più',
      messagePh: 'Composizione del gruppo, occasione, marina di partenza, note alimentari…',
      submit: 'Invia richiesta',
      note: 'Inviando accetti la nostra risposta entro 24h. Nessun pagamento richiesto in questa fase.',
      sending: 'Invio in corso…',
      error: 'Non siamo riusciti a inviare la richiesta. Scrivici direttamente a',
      privacy: 'I tuoi dati vengono inviati tramite il nostro provider (Formspree) solo per gestire la richiesta.',
    },
    cards: [
      { k: 'Email', v: CONTACT_EMAIL, sub: 'Prenotazioni e itinerari' },
      { k: 'Ormeggio', v: 'Marina di Poltu Quatu · Pontile 11', sub: 'Costa Smeralda, Sardegna' },
    ],
    success: {
      title: 'Grazie.',
      body: 'La tua richiesta ci è arrivata. Ti risponderemo entro 24 ore con disponibilità, preventivo e un itinerario su misura.',
      again: "Invia un'altra richiesta",
    },
    rate: {
      label: 'Tariffa giornaliera',
      seasons: [
        { k: 'Bassa stagione', v: '€3.800' },
        { k: 'Luglio', v: '€4.900' },
        { k: 'Agosto', v: '€5.500' },
      ],
      note: 'Carburante ed equipaggio non inclusi',
      addon: 'Seabob — €250 / giorno (opzionale)',
    },
  },
  yachtPage: {
    eyebrow: 'Lo Yacht',
    title: 'Un Pershing 58, italiano fino in fondo.',
    lede: 'Un hardtop planante di 17,7 metri. Due diesel MTU, eliche di superficie, scafo in Kevlar. Costruito nel 2013, italiano fino al motore.',
  },
  locationPage: {
    eyebrow: 'Porto Cervo',
    title: 'Porto Cervo, Costa Smeralda.',
    lede: 'Una cala di granito protetta sulla Costa Smeralda. Il Pershing 58 è ormeggiato al Pontile 11, riparato su tre lati, a mezz\'ora dall\'arcipelago della Maddalena.',
    facts: [
      { k: 'Ormeggio', v: 'Pontile 11' },
      { k: 'Marina', v: 'Poltu Quatu' },
      { k: 'Regione', v: 'Costa Smeralda' },
      { k: 'Stagione', v: 'Maggio – Ottobre' },
    ],
    intro: {
      eyebrow: 'La cala',
      title: 'Una cala nel granito.',
      body: [
        "La marina è in una spaccatura di granito rosa sulla Costa Smeralda, un canyon stretto che si nota solo dal mare. Superati i pilastri di roccia all'ingresso, la cala resta piatta con qualsiasi tempo.",
        "Il borgo sopra è piccolo e curato: una piazza, qualche ristorante, un hotel boutique. Gli ospiti lasciano i bagagli all'Hotel Pitrizza o a Cala di Volpe e raggiungono il pontile in cinque minuti; la barca è pronta, rifornita e approvvigionata al loro arrivo.",
      ],
      caption: "Poltu Quatu · marina all'alba",
      imageAlt: 'Marina di Poltu Quatu all\'alba, la barca al suo ormeggio',
    },
    strip: {
      eyebrow: 'In mare',
      title: 'Dalla cala al mare aperto.',
    },
    coords: {
      eyebrow: 'Acque di crociera',
      title: 'Dove naviga.',
      body: [
        'Il Pershing 58 ormeggia al Pontile 11 della Marina di Poltu Quatu, una cala stretta nel promontorio di granito. È protetta dal mare aperto su tre lati, a pochi passi dal borgo.',
        'Le acque si aprono sulle Bocche di Bonifacio e sull\'arcipelago della Maddalena: cinque isole protette entro un\'ora, scogli di granito, sabbia bianca e bassi fondali. Il Maestrale soffia da nord-ovest nel pomeriggio.',
        'Il miglior riparo è sottovento a Caprera, Spargi e Budelli, dove il fondale tiene e l\'ancora fa presa al primo tentativo.',
      ],
      items: [
        { label: 'Coordinate', value: '41°08′N · 9°32′E', sub: 'Pontile 11, Marina di Poltu Quatu' },
        { label: 'Profondità ormeggio', value: '10–40 m', sub: 'Granito + Posidonia oceanica' },
        { label: 'Vento dominante', value: 'Maestrale W–NW', sub: '8–15 nodi estate · raffiche a 30' },
        { label: 'Temperatura acqua', value: '22–26 °C', sub: 'Da giugno a settembre' },
        { label: 'Maree', value: '< 30 cm', sub: 'Trascurabili · bacino mediterraneo' },
      ],
    },
    inquiry: {
      eyebrow: 'Prenota',
      title: 'Organizza il tuo charter.',
      body: 'Raccontaci le date, il gruppo e che tipo di esperienza hai in mente. Rispondiamo entro 24 ore con disponibilità, preventivo e un itinerario.',
      cta: 'Inizia la richiesta',
    },
  },
  availabilityPage: {
    eyebrow: 'Disponibilità',
    title: 'Verifica le tue date.',
    lede: 'La disponibilità è su richiesta. Inviaci le date che preferisci qui sotto e confermiamo entro 24 ore.',
  },
  yachtMore: 'Altro dello yacht',
  footer: {
    rights: '© 2026 Riva Immobiliare S.r.l.',
    company: "P. IVA 13256780969 · Via Antonio Locatelli 14, 20851 Lissone (MB)",
    privacy: 'Privacy',
  },
  notFound: {
    eyebrow: 'Errore 404',
    title: 'Pagina non trovata.',
    lede: "L'indirizzo che hai seguito non esiste su questo sito. Il Pershing 58 ti aspetta a Porto Cervo.",
    home: 'Torna alla home',
  },
  privacy: {
    eyebrow: 'Note legali',
    title: 'Informativa sulla privacy',
    updated: 'Ultimo aggiornamento: 31 maggio 2026',
    intro: `La presente Informativa spiega come Riva Immobiliare S.r.l. ("noi") tratta i dati personali quando visiti questo sito e quando ci invii una richiesta di charter. Questo sito presenta l'offerta di charter nautico privato proposta con il marchio "Virtus" da Riva Immobiliare S.r.l.`,
    sections: [
      {
        heading: 'Titolare del trattamento',
        body: [
          `Il titolare del trattamento è Riva Immobiliare S.r.l., Via Antonio Locatelli 14, 20851 Lissone (MB), Italia. P. IVA 13256780969.`,
          `Per qualsiasi domanda su questa informativa o sui tuoi dati personali puoi scriverci all'indirizzo giorgio.l@virtussrl.it.`,
        ],
      },
      {
        heading: 'Quali dati raccogliamo',
        body: [
          `Trattiamo solo i dati che scegli di fornirci e i dati tecnici minimi necessari a erogare il sito:`,
        ],
        bullets: [
          `Modulo di richiesta: nome, indirizzo email, numero di ospiti, data preferita, durata del charter, tipo di charter ed eventuale messaggio. Vengono inviati solo quando invii il modulo.`,
          `Dati tecnici: quando il browser carica il sito, il tuo indirizzo IP e i dati di connessione standard sono trattati dai nostri fornitori di hosting e di distribuzione dei contenuti per erogare e proteggere il sito.`,
          `Preferenza di lingua: salviamo la lingua scelta (italiano o inglese) nella memoria locale del browser. Non contiene dati personali e serve solo a ricordare la tua scelta.`,
        ],
      },
      {
        heading: 'Perché li trattiamo e base giuridica',
        body: [`Trattiamo i tuoi dati sulle seguenti basi giuridiche previste dal GDPR:`],
        bullets: [
          `Per rispondere alla tua richiesta e contattarti riguardo a un possibile charter. Base giuridica: misure precontrattuali adottate su tua richiesta e nostro legittimo interesse a risponderti (art. 6, par. 1, lett. b) e f) GDPR).`,
          `Per erogare, proteggere e mantenere il sito. Base giuridica: nostro legittimo interesse a gestire un sito sicuro e funzionante (art. 6, par. 1, lett. f) GDPR).`,
        ],
      },
      {
        heading: 'A chi comunichiamo i dati',
        body: [
          `Non vendiamo i tuoi dati e non li utilizziamo per finalità pubblicitarie. Ci avvaliamo di un numero limitato di fornitori:`,
        ],
        bullets: [
          `Formspree (Formspree, Inc., Stati Uniti) riceve l'invio del modulo e lo inoltra alla nostra casella email. I dati del modulo sono trattati negli Stati Uniti.`,
          `Email: la tua richiesta viene recapitata alla casella giorgio.l@virtussrl.it. Se il modulo non può essere inviato, il sito propone un'alternativa che apre il tuo programma di posta con un messaggio già indirizzato alla stessa casella.`,
          `Cloudflare: il livello di distribuzione dei contenuti e di sicurezza che tratta i dati di connessione (incluso l'indirizzo IP) per erogare e proteggere il sito.`,
          `Google Fonts (Google): il sito carica i caratteri tipografici dai server di Google; in tale operazione il tuo indirizzo IP viene trasmesso a Google.`,
        ],
      },
      {
        heading: `Trasferimenti fuori dall'UE`,
        body: [
          `Alcuni dei fornitori sopra indicati (come Formspree e Google) hanno sede o trasferiscono dati negli Stati Uniti. In tali casi il trasferimento si basa su garanzie adeguate previste dal GDPR, come le Clausole Contrattuali Standard della Commissione europea o il Data Privacy Framework UE–USA.`,
        ],
      },
      {
        heading: 'Per quanto tempo li conserviamo',
        body: [
          `Conserviamo la tua richiesta e la relativa corrispondenza solo per il tempo necessario a gestirla e a mantenere una ragionevole documentazione dei nostri contatti, dopodiché vengono eliminate. La preferenza di lingua resta nel browser finché non la cancelli.`,
        ],
      },
      {
        heading: 'I tuoi diritti',
        body: [`Ai sensi del GDPR hai il diritto di:`],
        bullets: [
          `accedere ai tuoi dati personali e ottenerne copia;`,
          `far correggere i dati inesatti e integrare quelli incompleti;`,
          `ottenere la cancellazione dei dati o la limitazione del trattamento;`,
          `opporti al trattamento basato sul nostro legittimo interesse;`,
          `ricevere i tuoi dati in un formato portabile e leggibile da dispositivo automatico.`,
        ],
      },
      {
        heading: 'Come esercitare i diritti e reclami',
        body: [
          `Per esercitare questi diritti scrivici a giorgio.l@virtussrl.it. Hai inoltre il diritto di proporre reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it).`,
        ],
      },
      {
        heading: 'Cookie e tracciamento',
        body: [
          `Questo sito non utilizza cookie di tracciamento o pubblicitari e non utilizza alcuno strumento di analisi. L'unica informazione salvata nel browser è la preferenza di lingua descritta sopra, di natura tecnica.`,
        ],
      },
      {
        heading: 'Modifiche a questa informativa',
        body: [
          `Potremmo aggiornare periodicamente questa informativa. La versione pubblicata in questa pagina è sempre quella vigente e la data indicata in alto segnala l'ultima modifica.`,
        ],
      },
    ],
  },
};

export const COPY: Record<Lang, Copy> = { en: EN, it: IT };
